import { useState, useEffect, useCallback, useRef } from 'react'
import BackOfficeAPIClient from "../clients/BackofficeAPIClient"
import GroupSettings from "../types/GroupSettings"

interface UseGroupSettingsReturn {
    settings: GroupSettings | null
    loading: boolean
    saving: boolean
    hasChanges: boolean
    updateSetting: (key: string, value: unknown) => void
    saveSettings: () => Promise<boolean>
    resetSettings: () => void
}

const useGroupSettings = (): UseGroupSettingsReturn => {
    const backofficeApiClient = new BackOfficeAPIClient()
    const [settings, setSettings] = useState<GroupSettings | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [saving, setSaving] = useState<boolean>(false)
    const [hasChanges, setHasChanges] = useState<boolean>(false)
    const initialSettings = useRef<GroupSettings | null>(null)

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true)
            const data = await backofficeApiClient.get<GroupSettings>('/group-settings')
            setSettings(data)
            initialSettings.current = data
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchSettings()
    }, [fetchSettings])

    const updateSetting = useCallback((key: string, value: unknown) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        } as GroupSettings))
        setHasChanges(true)
    }, [])

    const saveSettings = useCallback(async () => {
        setSaving(true)
        try {
            await backofficeApiClient.put('/group-settings', settings)
            initialSettings.current = settings
            setHasChanges(false)
            return true
        } catch (error) {
            console.log(error)
            throw error
        } finally {
            setSaving(false)
        }
    }, [settings])

    const resetSettings = useCallback(() => {
        setSettings(initialSettings.current)
        setHasChanges(false)
    }, [])

    return {
        settings,
        loading,
        saving,
        hasChanges,
        updateSetting,
        saveSettings,
        resetSettings
    }
}

export default useGroupSettings
